/*
 * @providesModule SelectPaypalEmail
 *
 */

import React from 'react';
import {View, ScrollView, TouchableOpacity, Modal, StyleSheet, SafeAreaView} from 'react-native';
import _ from 'lodash';
import AccountInfoModal from 'AccountInfoModal';
import AccountManagementEmailCell from 'AccountManagementEmailCell';
import CurrentUserStore from 'CurrentUserStore';
import I18n from 'INTL';
import Label from 'Label';
import LoadingIndicator from 'LoadingIndicator';
import NavigationBar from 'NavigationBar';
import Optional from 'Optional';
import PushButton from 'PushButton';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';
import styles from 'SelectPaypalEmailStyle';
import SKZConstants from 'SKZConstants';
import SKZPersonalInfo from 'SKZPersonalInfo';
import {TextField} from 'react-native-material-textfield';

const {viewCornerRadiusSecondary} = SkillzStyle.fullStyles;

interface SelectPaypalEmailProps {
  selected: string;
  onSaveEmail?: Function;
  onBackPress?: Function;
  personalInfo: SKZPersonalInfo;
  fromWithdraw: boolean;
}

interface SelectPaypalEmailState {
  selected: number;
  personalInfo: SKZPersonalInfo;
  modal: {
    isModalVisible: boolean;
    title?: string;
    body?: string;
    hideCancel?: boolean;
  };
  userAddingEmail: boolean;
  emailList: Array<Object>;
}

export default class SelectPaypalEmail extends React.PureComponent<
  SelectPaypalEmailProps,
  SelectPaypalEmailState
> {
  static defaultProps = {
    onSaveEmail: () => {
      /* */
    },
    onBackPress: () => {
      /* */
    },
  };

  constructor(props: Object) {
    super(props);
    this.state = {
      selected: 0,
      personalInfo: props.personalInfo,
      modal: {
        isModalVisible: false,
        title: '',
        body: '',
        hideCancel: false,
      },
      userAddingEmail: false,
      emailList: [
        this.props.personalInfo.getUserPrimaryEmailData(),
        ...this.props.personalInfo.getUserSecondaryEmails(),
      ],
    };
  }

  componentDidMount() {
    this._selectCurrentEmail();
  }

  _onBackPress = () => {
    this.props.onBackPress?.(
      _.get(this.state.emailList[this.state.selected], 'email'),
      this.state.personalInfo
    );
  };

  _selectCurrentEmail() {
    if (this.props.selected) {
      for (let i = 0; i < this.state.emailList.length; i++) {
        if (this.state.emailList[i].email == this.props.selected) {
          this.setState({selected: i});
          return;
        }
      }
    }
  }

  _onSavePress = () => {
    this.props.onSaveEmail?.(
      _.get(this.state.emailList[this.state.selected], 'email'),
      this.state.personalInfo
    );
  };

  _updateSelectedEmail = (index: number) => {
    this.setState({selected: index});
  };

  _isEmailLimitReached(): boolean {
    return this.state.personalInfo.getAllUserEmails().length == SKZConstants.MAX_EMAIL_AMOUNT;
  }

  _isExistingEmail(email: string): boolean {
    const userEmails = this.state.personalInfo.getAllUserEmails();
    let isExistingEmail = false;
    userEmails.some((emailItem: Object): boolean => {
      if (emailItem.email.toUpperCase() == email.toUpperCase()) {
        isExistingEmail = true;
        return true;
      }
      return false;
    });
    return isExistingEmail;
  }

  _onAddEmailPress = () => {
    if (this._isEmailLimitReached()) {
      this._showModal(
        I18n.message('Max Emails Reached'),
        I18n.message('You may only have 5 emails at a time'),
        true
      );
      return;
    }

    this.setState({userAddingEmail: true});
  };

  _onAddEmailSubmit = (event?: Object) => {
    let email = '';

    if (_.get(event, 'nativeEvent', false)) {
      email = event.nativeEvent.text;
    } else {
      email = _.get(this._addEmailInput, 'state.text', '');
    }

    if (!email) {
      return;
    } else if (this._isExistingEmail(email)) {
      this._showModal(
        I18n.message('Error'),
        I18n.message('This email has already been added.'),
        true
      );
    } else {
      this._addEmail(email);
    }
  };

  _addEmail = (email: string) => {
    const newPersonalInfo = {
      ...this.state.personalInfo,
      secondary_emails: [...this.state.personalInfo.generateEmailMap().keys(), email],
    };

    LoadingIndicator.show();
    CurrentUserStore.updateUserPersonalInfo(newPersonalInfo)
      .then((updatedPersonalInfo: Object) => {
        if (updatedPersonalInfo) {
          const personalInfo = new SKZPersonalInfo(updatedPersonalInfo);
          this.setState({
            personalInfo,
            userAddingEmail: false,
            emailList: [
              personalInfo.getUserPrimaryEmailData(),
              ...personalInfo.getUserSecondaryEmails(),
            ],
          });
        }
        LoadingIndicator.hide();
      })
      .catch((error: unknown) => {
        LoadingIndicator.hide();
        this._showModal(I18n.message('Error'), error, true);
      });
  };

  _showModal(title: string, body: string, hideCancel: boolean) {
    this.setState({
      modal: {
        isModalVisible: true,
        title,
        body,
        hideCancel,
      },
    });
  }

  _hideModal = () => {
    this.setState({modal: {isModalVisible: false}});
  };

  _renderNavigationBar(): React.ReactNode {
    return (
      <NavigationBar
        hasBackButton
        onBackButtonPress={this._onBackPress}
        title={I18n.message('Select PayPal Email')}
      />
    );
  }

  _renderModal(): React.ReactNode {
    return (
      <Modal
        animationType='fade'
        transparent={true}
        onRequestClose={this._hideModal}
        visible={this.state.modal.isModalVisible}
      >
        <View
          style={[
            StyleSheet.absoluteFill,
            {maxWidth: Sizes.screenWidth, maxHeight: Sizes.screenHeight},
          ]}
        >
          <AccountInfoModal
            titleText={this.state.modal.title}
            bodyText={this.state.modal.body}
            hideCancelButton={this.state.modal.hideCancel}
            onDone={this._hideModal}
          />
        </View>
      </Modal>
    );
  }

  _renderContent(): React.ReactNode {
    return (
      <ScrollView style={styles.container}>
        {this._renderEmails()}
        {this._renderAddEmail()}
        {this._renderAddEmailRow()}
        {this._renderModal()}
        <Optional test={Sizes.isLandscape() && Sizes.isPhone()}>{this._renderFooter()}</Optional>
      </ScrollView>
    );
  }

  _renderEmails(): React.ReactNode {
    return this.state.emailList.map((item: Object, index: number): React.ReactNode => {
      return (
        <View key={index}>
          <AccountManagementEmailCell
            index={index}
            email={item.email}
            primary={item.primary}
            verified={item.verified}
            selectPayPalEnabled
            selectPayPalEmail={this._updateSelectedEmail}
            selectedForPayPal={Boolean(this.state.selected == index)}
          />
          <View style={styles.divider} />
        </View>
      );
    });
  }

  _renderAddEmail(): React.ReactNode | null {
    if (this.state.userAddingEmail) {
      return null;
    }

    return (
      <View>
        <TouchableOpacity
          style={styles.addEmailContainer}
          onPress={this._onAddEmailPress}
          accessibilityLabel={'Add Email'}
          accessible={true}
        >
          {this._renderPlus()}
          <Label style={styles.blueText}>{I18n.message('Add email')}</Label>
        </TouchableOpacity>
        <Optional test={!Sizes.isLandscape() && !Sizes.isPhone()}>
          <View style={styles.divider} />
        </Optional>
      </View>
    );
  }

  _renderPlus(): React.ReactNode {
    return <Label style={styles.plusSign}>{'+'}</Label>;
  }

  _renderAddEmailRow(): React.ReactNode | null {
    if (!this.state.userAddingEmail) {
      return null;
    }

    return (
      <View style={styles.addEmailInputContainer}>
        <View style={styles.addEmailInputWrapper}>
          <TextField
            ref={(ref: unknown) => {
              this._addEmailInput = ref;
            }}
            style={styles.addEmailInput}
            inputContainerPadding={4}
            labelHeight={32}
            label={I18n.message('email address')}
            labelTextStyle={styles.addEmailLabel}
            tintColor={'rgb(74, 144, 226)'}
            baseColor={'rgb(172, 172, 172)'}
            activeLineWidth={1}
            autoFocus={true}
            keyboardType={'email-address'}
            returnKeyType={'done'}
            onSubmitEditing={this._onAddEmailSubmit}
            autoCapitalize={'none'}
            autoCorrect={false}
            accessible={true}
            accessibilityLabel={'new_email_field'}
          />
        </View>
        <PushButton
          style={styles.addEmailInputButton}
          onPress={this._onAddEmailSubmit}
          colors={['#FFF']}
          title={I18n.message('Add')}
          textStyle={styles.addEmailInputButtonText}
          cornerRadius={viewCornerRadiusSecondary.borderRadius}
        />
      </View>
    );
  }

  _renderFooter(): React.ReactNode {
    return (
      <View style={styles.footer}>
        <PushButton
          style={styles.saveButton}
          onPress={this._onSavePress}
          colors={['rgb(74, 144, 226)']}
          title={I18n.message('Save')}
          textStyle={styles.saveButtonText}
          cornerRadius={viewCornerRadiusSecondary.borderRadius}
        />
      </View>
    );
  }

  _renderPhone(): React.ReactNode {
    return (
      <View style={styles.container}>
        {this._renderNavigationBar()}
        {this._renderContent()}
        {this._renderFooter()}
      </View>
    );
  }

  _renderPhoneLandscape(): React.ReactNode {
    return (
      <View style={styles.landscapeAndTabletBG}>
        {this._renderNavigationBar()}
        <View style={styles.landscapePhoneContainer}>{this._renderContent()}</View>
      </View>
    );
  }

  _renderTablet(): React.ReactNode {
    return (
      <View style={styles.landscapeAndTabletBG}>
        {this._renderNavigationBar()}
        <View style={styles.tabletContainer}>
          {this._renderContent()}
          {this._renderFooter()}
        </View>
      </View>
    );
  }

  render(): React.ReactNode {
    const content = Sizes.select({
      tablet: this._renderTablet(),
      phoneLandscape: this._renderPhoneLandscape(),
      other: this._renderPhone(),
    });
    return <SafeAreaView style={StyleSheet.absoluteFill}>{content}</SafeAreaView>;
  }
}
